/**
 * Sentry 统一配置接口
 * 支持功能开关和参数配置
 */
export interface SentryConfig {
  // 基础配置
  dsn: string
  environment?: string
  release?: string

  // 功能开关
  features: {
    // 错误监控，默认开启
    errorMonitoring?: boolean
    // 性能监控，默认开启
    performance: boolean
    // 会话回放，默认开启
    sessionReplay: boolean
  }

  // 性能监控配置
  performance?: {
    /** 性能追踪采样率，控制有多少百分比的页面加载和导航会被追踪 (0.0-1.0) */
    tracesSampleRate?: number
    /** 性能分析采样率，控制有多少百分比的性能分析数据会被收集 (0.0-1.0) */
    profilesSampleRate?: number
    /** 是否启用长任务监控，用于检测阻塞主线程的长时间运行任务 */
    enableLongTask?: boolean
    /** 是否启用交互到下一次绘制(INP)监控，衡量用户交互的响应性 */
    enableInp?: boolean
  }

  // 错误过滤配置
  errorFilter?: {
    /** 是否启用网络错误过滤，过滤掉网络请求失败等常见网络错误 */
    enableNetworkErrorFilter?: boolean
    /** 是否启用内容安全策略(CSP)错误过滤，过滤掉CSP违规错误 */
    enableCSPErrorFilter?: boolean
    /** 是否启用请求中止错误过滤，过滤掉用户主动取消的请求错误 */
    enableAbortErrorFilter?: boolean
    /** 是否启用ResizeObserver错误过滤，过滤掉元素大小变化观察器错误 */
    enableResizeObserverFilter?: boolean
    /** 自定义错误过滤器数组，可以添加自定义的错误过滤逻辑 */
    customFilters?: Array<(error: Error) => boolean>
  }

  // 会话回放配置
  sessionReplay?: {
    /** 会话回放采样率，控制有多少百分比的用户会话会被录制 (0.0-1.0) */
    sessionSampleRate?: number
    /** 错误回放采样率，控制有多少百分比的错误会话会被录制 (0.0-1.0) */
    errorSampleRate?: number
    /** 是否屏蔽所有输入框内容，保护用户隐私信息 */
    maskAllInputs?: boolean
    /** 是否屏蔽所有文本内容，保护用户隐私信息 */
    maskAllText?: boolean
  }

  // 标签和上下文
  tags?: Record<string, string>
  context?: Record<string, any>
}

/**
 * 默认配置
 */
export const defaultSentryConfig: SentryConfig = {
  dsn: '',
  environment: process.env.NODE_ENV || 'development',
  release: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',

  features: {
    errorMonitoring: true,
    performance: true,
    sessionReplay: true,
  },

  performance: {
    tracesSampleRate: 0.3,
    profilesSampleRate: 0.1,
    enableLongTask: false,
    enableInp: false,
  },

  errorFilter: {
    enableNetworkErrorFilter: true,
    enableCSPErrorFilter: true,
    enableAbortErrorFilter: true,
    enableResizeObserverFilter: true,
    customFilters: [],
  },

  sessionReplay: {
    sessionSampleRate: 0.1,
    errorSampleRate: 0.3,
    maskAllInputs: false,
    maskAllText: false,
  },

  tags: {},
  context: {},
}
